import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MessageSquare,
  Send,
  ArrowLeft,
  Star,
  Calendar,
  Dumbbell,
  Plus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { commentsAPI, studentsAPI } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { inputClass } from '../lib/validation';
import type { Student } from '../types';
import clsx from 'clsx';

function timeAgo(iso: string): string {
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 1) return 'recién';
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `hace ${days} d`;
  return d.toLocaleDateString('es-AR');
}

function initials(first?: string, last?: string) {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}

// ============================================================
// Hilo de conversación con un alumno
// ============================================================
function Thread({
  studentId,
  studentName,
  onBack,
}: {
  studentId: string;
  studentName: string;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');

  const threadQuery = useQuery({
    queryKey: ['thread', studentId],
    queryFn: () => commentsAPI.getThread(studentId),
    refetchInterval: 20000,
  });

  const sendMutation = useMutation({
    mutationFn: () =>
      commentsAPI.create({ content: text.trim(), type: 'general', studentId }),
    onSuccess: () => {
      setText('');
      queryClient.invalidateQueries({ queryKey: ['thread', studentId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const messages = threadQuery.data ?? [];

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (text.trim()) sendMutation.mutate();
  };

  return (
    <div className="card flex flex-col h-[70vh]">
      {/* Header */}
      <div className="flex items-center gap-3 pb-3 border-b border-dark-700/50">
        <button onClick={onBack} className="lg:hidden p-1 text-dark-400 hover:text-dark-100" title="Volver">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-9 h-9 rounded-full bg-dark-700 ring-1 ring-primary-500/30 flex items-center justify-center text-dark-50 text-sm font-medium">
          {initials(studentName.split(' ')[0], studentName.split(' ')[1])}
        </div>
        <Link to={`/students/${studentId}`} className="font-semibold text-dark-100 hover:text-primary-400 transition-colors">
          {studentName}
        </Link>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-3">
        {threadQuery.isLoading ? (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        ) : messages.length === 0 ? (
          <div className="text-center text-dark-400 text-sm py-10">
            Todavía no hay mensajes. Escribile a {studentName.split(' ')[0]}.
          </div>
        ) : (
          messages.map((m: any) => {
            const fromTrainer = m.userId !== studentId;
            return (
              <div key={m.id} className={clsx('flex', fromTrainer ? 'justify-end' : 'justify-start')}>
                <div
                  className={clsx(
                    'max-w-[78%] rounded-2xl px-4 py-2.5',
                    fromTrainer
                      ? 'bg-primary-500/15 border border-primary-500/25 rounded-br-md'
                      : 'bg-dark-800 border border-dark-700 rounded-bl-md'
                  )}
                >
                  {/* Contexto (semana / ejercicio) */}
                  {m.week && (
                    <p className="text-[11px] text-dark-400 mb-1 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {m.week.plan?.name ? `${m.week.plan.name} · ` : ''}Semana {m.week.weekNumber}
                    </p>
                  )}
                  {m.blockItem?.exercise && (
                    <p className="text-[11px] text-dark-400 mb-1 flex items-center gap-1">
                      <Dumbbell className="w-3 h-3" />
                      {m.blockItem.exercise.name}
                    </p>
                  )}
                  {/* Rating */}
                  {m.rating ? (
                    <div className="flex items-center gap-0.5 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={clsx('w-3 h-3', i < m.rating ? 'text-primary-400 fill-primary-400' : 'text-dark-600')}
                        />
                      ))}
                    </div>
                  ) : null}
                  <p className="text-sm text-dark-100 whitespace-pre-wrap break-words">{m.content}</p>
                  <p className="text-[10px] text-dark-500 mt-1 text-right">{timeAgo(m.createdAt)}</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Composer */}
      <form onSubmit={handleSend} className="pt-3 border-t border-dark-700/50 flex items-end gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend(e);
          }}
          rows={2}
          placeholder="Escribí tu respuesta… (Ctrl+Enter para enviar)"
          className={inputClass(false) + ' resize-none flex-1'}
        />
        <button
          type="submit"
          disabled={!text.trim() || sendMutation.isPending}
          className="btn-primary !px-3 !py-3"
          title="Enviar"
        >
          {sendMutation.isPending ? <LoadingSpinner size="sm" /> : <Send className="w-5 h-5" />}
        </button>
      </form>
    </div>
  );
}

// ============================================================
// Página de mensajes (bandeja + hilo)
// ============================================================
export function Comments() {
  const [selected, setSelected] = useState<{ id: string; name: string } | null>(null);
  const [newStudentId, setNewStudentId] = useState('');

  const convQuery = useQuery({
    queryKey: ['conversations'],
    queryFn: () => commentsAPI.getConversations(),
    refetchInterval: 30000,
  });
  const { data: students } = useQuery({
    queryKey: ['students'],
    queryFn: () => studentsAPI.getAll(),
  });

  const conversations = convQuery.data ?? [];

  const startConversation = (id: string) => {
    const s = students?.find((st: Student) => st.id === id);
    if (s) setSelected({ id, name: `${s.firstName} ${s.lastName}` });
    setNewStudentId('');
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div>
        <h1 className="section-title flex items-center gap-3">
          <MessageSquare className="w-8 h-8 text-dark-100" />
          Mensajes
        </h1>
        <p className="section-subtitle">Conversaciones con tus alumnos: feedback y respuestas.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Bandeja */}
        <div className={clsx('lg:col-span-1 space-y-3', selected && 'hidden lg:block')}>
          {/* Iniciar conversación */}
          <div className="card !p-3">
            <label className="text-xs text-dark-400 mb-1.5 flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Escribir a un alumno
            </label>
            <select
              value={newStudentId}
              onChange={(e) => e.target.value && startConversation(e.target.value)}
              className={inputClass(false)}
            >
              <option value="">Elegí un alumno…</option>
              {students?.map((s: Student) => (
                <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>
              ))}
            </select>
          </div>

          {convQuery.isLoading ? (
            <div className="flex justify-center py-8"><LoadingSpinner /></div>
          ) : conversations.length === 0 ? (
            <div className="card text-center py-10">
              <MessageSquare className="w-12 h-12 mx-auto text-dark-600 mb-3" />
              <p className="text-sm text-dark-400">Sin conversaciones todavía.</p>
            </div>
          ) : (
            conversations.map((conv: any) => {
              const isActive = selected?.id === conv.student.id;
              return (
                <button
                  key={conv.student.id}
                  onClick={() => setSelected({ id: conv.student.id, name: `${conv.student.firstName} ${conv.student.lastName}` })}
                  className={clsx(
                    'w-full text-left card !p-3 flex items-center gap-3 transition-colors',
                    isActive ? 'border-primary-500/40' : 'hover:border-dark-600'
                  )}
                >
                  <div className="w-10 h-10 rounded-full bg-dark-700 ring-1 ring-primary-500/30 flex items-center justify-center text-dark-50 text-sm font-medium flex-shrink-0">
                    {initials(conv.student.firstName, conv.student.lastName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-dark-100 truncate">
                        {conv.student.firstName} {conv.student.lastName}
                      </span>
                      <span className="text-[10px] text-dark-500 flex-shrink-0">{timeAgo(conv.lastMessage.createdAt)}</span>
                    </div>
                    <p className="text-xs text-dark-400 truncate">
                      {conv.lastMessage.fromTrainer ? 'Vos: ' : ''}{conv.lastMessage.content}
                    </p>
                  </div>
                  {conv.unread > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-primary-500 text-dark-950 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                      {conv.unread}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Hilo */}
        <div className={clsx('lg:col-span-2', !selected && 'hidden lg:block')}>
          {selected ? (
            <Thread studentId={selected.id} studentName={selected.name} onBack={() => setSelected(null)} />
          ) : (
            <div className="card flex flex-col items-center justify-center text-center h-[70vh] text-dark-400">
              <MessageSquare className="w-16 h-16 text-dark-600 mb-4" />
              <p>Elegí una conversación para ver el hilo.</p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
